import React, { useEffect, useRef, useState } from 'react';

import maplibregl, {ScaleControl} from 'maplibre-gl';

import { STYLE, MAP_BOUNDS, MAP_CENTRE, MAP_ATTRIBUTION } from '../../utils/mapConsts';
import { BREAKPOINTS } from '../../utils/consts';

import { GovType, SelectedLayersType } from '../../types/dataTypes';
import { MapStyle } from '../../styles/styles';

import GovLayer from './MapLayers/GovLayer';
import InfrastructureLayers from './MapLayers/InfrastructureLayers';
import ProlyDrawCalcElement from './ProlyDrawCalcElement';
import { library } from "@fortawesome/fontawesome-svg-core";
import { far } from "@fortawesome/free-regular-svg-icons";
import { fa2, fa5, fas } from "@fortawesome/free-solid-svg-icons";
import { SELECTED_LAYER_INIITIAL } from '../../utils/consts';
import Aside from '../Shared/Aside';
import Layers from '../Layers/Layers';
import Sidebar from './Sidebar/Sidebar';
import Legend from '../Legend/Legend';
import Footer from '../Shared/Footer';
import styled from '@emotion/styled';
import { searchParamsObj } from '../../utils/dataHandler';
import SettlementLayer from './MapLayers/SettlementLayer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faListUl } from '@fortawesome/free-solid-svg-icons';

type MapProps = {
}

function Map({}: MapProps) {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobileLegendOpen, setIsMobileLegendOpen] = useState(false);
  const [selectedLayers, setSelectedLayers] = useState<SelectedLayersType[]>(SELECTED_LAYER_INIITIAL)
  const [govData, setGovData] = useState<GovType>({ features: undefined });
  const [gov_adm1, setGovAdm1] = useState<GovType>({ features: undefined });
  const [numberSelectedSettlements, setNumberSelectedSettlements] = useState<number>(0);

  const map = useRef<maplibregl.Map | null>(null);
  library.add(far, fas);

  const mapContainerRef = useRef<any>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (map.current) return; // initialize map only once

    map.current = new maplibregl.Map({
      container: mapContainerRef.current,
      style: STYLE,
      center: MAP_CENTRE,
      zoom: 8,
      clickTolerance: 10,
      minZoom: 6, // how far out we can go
      maxZoom: 16, // how far in we can go
      attributionControl: false,
      // maxBounds: MAX_BOUNDS
    });

    map.current.on('load', function () {
      setMapLoaded(true);
      if (map.current) {
        map.current.dragRotate.disable();
        map.current.touchZoomRotate.disableRotation();
        map.current.touchPitch.disable();
        let scale = new ScaleControl({
          maxWidth: 200,
          unit: 'metric',
        });
        map.current.addControl(scale, 'bottom-left');
        map.current.addControl(new maplibregl.AttributionControl({
          compact: false,
          customAttribution: MAP_ATTRIBUTION,
        }), 'bottom-right');
        map.current.addControl(new maplibregl.NavigationControl({
          visualizePitch: true,
          showZoom: true,
          showCompass: true
        }), 'top-right');
        // Add margin to move the controls down
        const navControl = document.querySelector('.maplibregl-ctrl-top-right');
        if (navControl) {
          (navControl as HTMLElement).style.marginTop = '85px';
        }

        if (window.matchMedia(`(max-width: ${BREAKPOINTS.MOBILE})`).matches) {
          map.current.fitBounds(MAP_BOUNDS, {
            padding: { top: 96, right: 20, bottom: 120, left: 20 },
            duration: 0,
          });
        }
      }
    });
    
  }, [map, setMapLoaded]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(`(max-width: ${BREAKPOINTS.MOBILE})`);
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsMobileView(event.matches);
      if (!event.matches) {
        setIsMobileSidebarOpen(false);
        setIsMobileLegendOpen(false);
      }
    };

    handleChange(mediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return (
    <>
    <MapContainer>
      <MapStyle ref={mapContainerRef} />
      <MobileSidebarBackdrop isOpen={isMobileSidebarOpen} onClick={() => setIsMobileSidebarOpen(false)} />
        {govData&& <Sidebar map={map} selectedLayers={selectedLayers} setSelectedLayers={setSelectedLayers} 
        gov_adm1={gov_adm1} numberSelectedSettlements= {numberSelectedSettlements} isMobileOpen={isMobileSidebarOpen} onCloseMobile={() => setIsMobileSidebarOpen(false)}/>}
      <MobileSidebarToggle type="button" onClick={() => setIsMobileSidebarOpen(true)}>
        <FontAwesomeIcon icon={faLayerGroup} />
        Layers & filters
      </MobileSidebarToggle>
      <MobileLegendToggle
        type="button"
        aria-label={isMobileLegendOpen ? 'Hide legend' : 'Show legend'}
        aria-expanded={isMobileLegendOpen}
        onClick={() => setIsMobileLegendOpen((prev) => !prev)}
      >
        <FontAwesomeIcon icon={faListUl} />
      </MobileLegendToggle>
      <Aside>
          <Layers map={map} />
      </Aside>
      {mapLoaded&& <ProlyDrawCalcElement map={map} control_position={'bottom-left'}/>}
      {mapLoaded&& <GovLayer map={map} mapLoaded={mapLoaded} setGovData={setGovData} setGovAdm1={setGovAdm1}/>}
      {mapLoaded&& <SettlementLayer map={map} mapLoaded={mapLoaded} dynamic_radius={false} 
        setNumberSelectedSettlements={setNumberSelectedSettlements}/>}
      {(!isMobileView || isMobileLegendOpen) && (
        <Aside right bottom zIndex={10000}>
          <div style={{ display: "flex" }}>
            <Legend map={map} selectedLayers={selectedLayers}/>
          </div>
        </Aside>
      )}
      {mapLoaded&&<InfrastructureLayers map={map} mapLoaded={mapLoaded} selectedLayers={selectedLayers}/>}
      </MapContainer>
      <Footer />
      </>
  )
}

export default Map;

const MapContainer = styled.div<{}>(() => `
  position: relative;
  display: flex;
  width: 100%;
  height: 100vh;
  font-size: 14px !important;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    height: calc(100vh - 64px);
    min-height: calc(100svh - 64px);
    margin-top: 64px;
  }
`);

const MobileSidebarToggle = styled.button`
  display: none;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    position: absolute;
    top: 14px;
    left: 12px;
    z-index: 1002;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 999px;
    background: white;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
    color: #0A2848;
    pointer-events: auto;
  }
`;

const MobileSidebarBackdrop = styled.button<{ isOpen: boolean }>(({ isOpen }) => `
  display: none;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    display: ${isOpen ? 'block' : 'none'};
    position: absolute;
    inset: 0;
    z-index: 1000;
    background: rgba(10, 40, 72, 0.22);
  }
`);

const MobileLegendToggle = styled.button`
  display: none;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    position: absolute;
    right: 12px;
    bottom: 84px;
    z-index: 1002;
    width: 42px;
    height: 42px;
    border-radius: 999px;
    background: white;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
    color: #0A2848;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
  }
`;